import type { Driver } from '@multirx/shared';
import {
  formatDriverTitleChips,
  type DriverSeasonProfile,
} from '@multirx/shared/game/driverPalmares.js';
import { isOwnDriver } from '@multirx/shared/game/driverDisplayLabel.js';
import { getOwnDriverId } from '../auth/ownDriverLabel.js';
import { fetchDriverById } from '../drivers/api.js';
import { fetchDriverSeasonProfile } from '../championship/api.js';
import { buildPlayerProfileTooltipHtml } from '../game/playerProfileTooltip.js';
import {
  cancelTooltipLeave,
  closeTooltipFade,
  closeTooltipSlideLeft,
  isTooltipSlideOpen,
  openTooltipFade,
  openTooltipSlideLeft,
} from '../ui/tooltipFade.js';

const TOOLTIP_GAP = 10;
const ROW_SELECTOR = '[data-driver-id][data-standings-rank]';
const PROFILE_WIDTH_FALLBACK = 352; // 22rem
const driverCache = new Map<number, Promise<Driver | null>>();
const profileCache = new Map<string, Promise<DriverSeasonProfile | null>>();

/** Rang classement en français pour le filigrane photo. */
export function standingsRankLabel(rank: number): string {
  if (rank === 1) return '1er';
  return `${rank}ème`;
}

let comboEl: HTMLDivElement | null = null;
let profileEl: HTMLDivElement | null = null;
let pointsEl: HTMLDivElement | null = null;
let tooltipAnchor: HTMLElement | null = null;
let hoveredRow: HTMLElement | null = null;
/** Empêche la fermeture (épinglage du détail points). */
let holdOpen = false;
let showGeneration = 0;
let globalDismissListeners = false;
let comboHoverListeners = false;

export type StandingsTooltipRowMeta = {
  driverId: number;
  rank: number;
  points: number;
  driverName?: string;
  division?: number;
  seasonId?: number;
};

export function buildStandingsDriverTooltipHtml(
  driver: Pick<
    Driver,
    'firstName' | 'lastName' | 'nationality' | 'imagePath' | 'pilotage' | 'defense' | 'division'
  >,
  meta: Pick<StandingsTooltipRowMeta, 'driverId' | 'rank' | 'points' | 'division'>,
  profile?: DriverSeasonProfile | null
): string {
  const driverName = `${driver.firstName} ${driver.lastName}`.trim();
  const division = meta.division ?? driver.division;
  const handle = division != null ? `DIVISION D${division}` : 'CHAMPIONNAT';
  const season = profile?.season;
  const titles = profile?.titles?.length ? formatDriverTitleChips(profile.titles) : [];
  const stats = [
    { value: `★${driver.pilotage ?? 0}`, label: 'Pilotage' },
    { value: `★${driver.defense ?? 0}`, label: 'Défense' },
    {
      value: String(season?.overtakesSucceeded ?? 0),
      label: 'Dépass. réussis',
    },
    {
      value: String(season?.overtakesCountered ?? 0),
      label: 'Dépass. contrés',
    },
  ];
  const highlights: string[] = [];
  if (season) {
    if (season.finalsWon > 0) {
      highlights.push(
        `${season.finalsWon} finale${season.finalsWon > 1 ? 's' : ''} gagnée${season.finalsWon > 1 ? 's' : ''}`
      );
    }
    if (season.podiums > 0) {
      highlights.push(`${season.podiums} podium${season.podiums > 1 ? 's' : ''} demi/finale`);
    }
    for (const tour of season.bestMeetingTours) {
      const circuit = tour.circuitName?.trim();
      highlights.push(
        circuit
          ? `M${tour.meetingNumber} · ${circuit} · ${tour.diceRolls} lancers`
          : `Meeting ${tour.meetingNumber} · ${tour.diceRolls} lancers`
      );
    }
  }

  return buildPlayerProfileTooltipHtml({
    driverName,
    carName: null,
    driverImagePath: driver.imagePath ?? null,
    driverDivision: division,
    nationalityCode: driver.nationality,
    isOwnDriver: isOwnDriver(meta.driverId, getOwnDriverId()),
    rank: meta.rank,
    rankLabel: standingsRankLabel(meta.rank),
    statusLabel: `${meta.points} PTS`,
    statusVariant: 'waiting',
    handle,
    bio: null,
    titles,
    stats,
    highlights: highlights.length > 0 ? highlights : undefined,
    footer: null,
  });
}

function buildFallbackTooltipHtml(meta: StandingsTooltipRowMeta): string {
  const name = meta.driverName?.trim() || `Pilote #${meta.driverId}`;
  const handle = meta.division != null ? `DIVISION D${meta.division}` : 'CHAMPIONNAT';
  return buildPlayerProfileTooltipHtml({
    driverName: name,
    carName: null,
    isOwnDriver: isOwnDriver(meta.driverId, getOwnDriverId()),
    rank: meta.rank,
    rankLabel: standingsRankLabel(meta.rank),
    statusLabel: `${meta.points} PTS`,
    statusVariant: 'waiting',
    handle,
    bio: null,
    stats: [{ value: '—', label: 'Profil indisponible' }],
    footer: null,
  });
}

function getDriverCached(driverId: number): Promise<Driver | null> {
  let pending = driverCache.get(driverId);
  if (!pending) {
    pending = fetchDriverById(driverId)
      .then((d) => d)
      .catch(() => null);
    driverCache.set(driverId, pending);
  }
  return pending;
}

function getProfileCached(
  seasonId: number | undefined,
  driverId: number
): Promise<DriverSeasonProfile | null> {
  if (seasonId == null || seasonId <= 0) return Promise.resolve(null);
  const key = `${seasonId}:${driverId}`;
  let pending = profileCache.get(key);
  if (!pending) {
    pending = fetchDriverSeasonProfile(seasonId, driverId).catch(() => null);
    profileCache.set(key, pending);
  }
  return pending;
}

function sameDriverRow(a: HTMLElement | null, b: HTMLElement | null): boolean {
  if (!a || !b) return false;
  if (a === b) return true;
  const id = a.dataset.driverId;
  return id != null && id !== '' && id === b.dataset.driverId;
}

function findLiveStandingsRow(driverId: string): HTMLElement | null {
  const safeId = driverId.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  return document.querySelector(`${ROW_SELECTOR}[data-driver-id="${safeId}"]`);
}

/** Ancre connectée (sinon re-query par data-driver-id). */
function resolvePositionAnchor(anchor: HTMLElement): HTMLElement | null {
  if (anchor.isConnected) return anchor;
  const id = anchor.dataset.driverId;
  if (!id) return null;
  return findLiveStandingsRow(id);
}

function profilePanelWidth(): number {
  const card = profileEl?.querySelector<HTMLElement>('.card-profile');
  const w = card?.offsetWidth || profileEl?.offsetWidth || 0;
  return w > 0 ? w : PROFILE_WIDTH_FALLBACK;
}

function ensureComboHoverListeners(): void {
  if (comboHoverListeners || !comboEl) return;
  comboHoverListeners = true;
  comboEl.addEventListener('mouseenter', () => {
    if (tooltipAnchor) hoveredRow = tooltipAnchor;
  });
  comboEl.addEventListener('mouseleave', () => {
    if (holdOpen) return;
    hoveredRow = null;
    cancelPendingShow();
    hideCombo();
  });
}

function getComboEl(): HTMLDivElement {
  if (comboEl && !comboEl.isConnected) {
    comboEl = null;
    profileEl = null;
    pointsEl = null;
    comboHoverListeners = false;
  }
  if (!comboEl) {
    comboEl = document.createElement('div');
    comboEl.className = 'standings-combo-tooltip';
    comboEl.hidden = true;
    comboEl.setAttribute('role', 'presentation');

    profileEl = document.createElement('div');
    profileEl.className = 'standings-combo-tooltip__profile race-profile-tooltip';

    pointsEl = document.createElement('div');
    pointsEl.className = 'standings-combo-tooltip__points';
    pointsEl.hidden = true;

    comboEl.append(profileEl, pointsEl);
    document.body.appendChild(comboEl);
    ensureGlobalDismissListeners();
    ensureComboHoverListeners();
  }
  return comboEl;
}

function pointsPanelActive(): boolean {
  return pointsEl != null && !pointsEl.hidden;
}

function syncComboClasses(): void {
  const combo = comboEl;
  if (!combo) return;
  combo.classList.toggle('standings-combo-tooltip--with-points', pointsPanelActive());
  combo.classList.toggle('is-pinned', holdOpen);
}

function clearPointsPanelContent(): void {
  if (!pointsEl) return;
  pointsEl.innerHTML = '';
  pointsEl.style.height = '';
  pointsEl.style.maxHeight = '';
  pointsEl.classList.remove('is-slide-open', 'is-slide-leaving');
}

/** Hauteur du panneau points = hauteur naturelle du profil (scroll interne). */
function syncPointsHeightToProfile(): void {
  if (!pointsEl || !profileEl || !pointsPanelActive()) {
    if (pointsEl && pointsEl.hidden) {
      pointsEl.style.height = '';
      pointsEl.style.maxHeight = '';
    }
    return;
  }
  const profileHeight = Math.ceil(
    profileEl.getBoundingClientRect().height || profileEl.offsetHeight
  );
  if (profileHeight <= 0) return;
  pointsEl.style.height = `${profileHeight}px`;
  pointsEl.style.maxHeight = `${profileHeight}px`;
}

/**
 * Centre le bloc visible (profil, ou profil+points) sur la ligne.
 * Clamp viewport uniquement si débordement.
 */
function positionCombo(anchor: HTMLElement): void {
  const combo = getComboEl();
  const resolved = resolvePositionAnchor(anchor);
  if (!resolved) {
    if (!holdOpen) hideCombo();
    return;
  }
  tooltipAnchor = resolved;
  syncPointsHeightToProfile();

  const anchorRect = resolved.getBoundingClientRect();
  const profileWidth = profilePanelWidth();
  const withPoints = pointsEl != null && !pointsEl.hidden;
  // Shrink-wrap avant mesure (évite largeur = viewport → clamp à gauche).
  combo.style.width = 'max-content';
  const tipRect = combo.getBoundingClientRect();
  // Sans points : largeur = profil seul. Avec points : largeur réelle du combo.
  const tipWidth = withPoints ? (tipRect.width > 0 ? tipRect.width : profileWidth) : profileWidth;
  const tipHeight = tipRect.height > 0 ? tipRect.height : 1;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const pad = 8;

  // Centre le bloc visible (profil seul, ou profil+points) sur la ligne.
  let left = anchorRect.left + anchorRect.width / 2 - tipWidth / 2;
  left = Math.max(pad, Math.min(left, vw - tipWidth - pad));

  let top = anchorRect.top - tipHeight - TOOLTIP_GAP;
  if (top < pad) {
    top = anchorRect.bottom + TOOLTIP_GAP;
    combo.classList.add('is-below-anchor');
  } else {
    combo.classList.remove('is-below-anchor');
  }
  top = Math.max(pad, Math.min(top, vh - tipHeight - pad));

  combo.style.left = `${Math.round(left)}px`;
  combo.style.top = `${Math.round(top)}px`;
}

function hideCombo(): void {
  if (holdOpen) return;
  tooltipAnchor = null;
  if (comboEl) {
    closeTooltipFade(comboEl, () => {
      if (comboEl) {
        comboEl.style.left = '';
        comboEl.style.top = '';
      }
    });
  }
  if (pointsEl && !pointsEl.hidden) {
    const leavingPoints = pointsEl;
    closeTooltipSlideLeft(leavingPoints, () => {
      if (pointsEl !== leavingPoints) return;
      clearPointsPanelContent();
      syncComboClasses();
    });
  }
  syncComboClasses();
}

function cancelPendingShow(): void {
  showGeneration += 1;
}

function dismissTooltip(): void {
  if (holdOpen) return;
  hoveredRow = null;
  cancelPendingShow();
  hideCombo();
}

function isActiveDriverRow(row: HTMLElement): boolean {
  const id = row.dataset.driverId;
  if (!id) return false;
  return hoveredRow?.dataset.driverId === id || tooltipAnchor?.dataset.driverId === id;
}

function showProfileHtml(anchor: HTMLElement, html: string, generation: number): void {
  if (generation !== showGeneration) return;
  if (!holdOpen && !sameDriverRow(hoveredRow, anchor)) return;
  const combo = getComboEl();
  if (!profileEl) return;
  tooltipAnchor = anchor;
  profileEl.innerHTML = html;
  openTooltipFade(combo);
  syncComboClasses();
  // Sync immédiat + rAF : ne jamais rester visible à (0,0).
  positionCombo(anchor);
  requestAnimationFrame(() => {
    if (generation !== showGeneration) return;
    if (!holdOpen && !sameDriverRow(hoveredRow, anchor) && !sameDriverRow(tooltipAnchor, anchor)) {
      return;
    }
    positionCombo(tooltipAnchor ?? anchor);
  });
}

function scheduleTooltipForRow(row: HTMLElement, meta: StandingsTooltipRowMeta): void {
  hoveredRow = row;
  const generation = ++showGeneration;
  const driverId = String(meta.driverId);

  void Promise.all([
    getDriverCached(meta.driverId),
    getProfileCached(meta.seasonId, meta.driverId),
  ]).then(([driver, profile]) => {
    if (generation !== showGeneration) return;
    if (!holdOpen && hoveredRow?.dataset.driverId !== driverId) return;
    const live = hoveredRow?.isConnected ? hoveredRow : (findLiveStandingsRow(driverId) ?? row);
    const html = driver
      ? buildStandingsDriverTooltipHtml(driver, meta, profile)
      : buildFallbackTooltipHtml(meta);
    showProfileHtml(live, html, generation);
  });
}

export function readStandingsRowMeta(row: HTMLElement): StandingsTooltipRowMeta | null {
  const driverId = parseInt(row.dataset.driverId ?? '', 10);
  const rank = parseInt(row.dataset.standingsRank ?? '', 10);
  const points = parseInt(row.dataset.standingsPoints ?? '', 10);
  if (!Number.isFinite(driverId) || driverId <= 0) return null;
  if (!Number.isFinite(rank) || rank <= 0) return null;
  if (!Number.isFinite(points) || points < 0) return null;

  const divisionRaw =
    row.dataset.division ?? row.closest<HTMLElement>('[data-division]')?.dataset.division;
  const division = divisionRaw != null ? parseInt(divisionRaw, 10) : undefined;
  const seasonRaw =
    row.dataset.seasonId ?? row.closest<HTMLElement>('[data-season-id]')?.dataset.seasonId;
  const seasonId = seasonRaw != null ? parseInt(seasonRaw, 10) : undefined;

  return {
    driverId,
    rank,
    points,
    driverName: row.dataset.driverName,
    division: Number.isFinite(division) ? division : undefined,
    seasonId: Number.isFinite(seasonId) ? seasonId : undefined,
  };
}

/** Assure le panneau profil pour la ligne du pilote (ex. survol d’une pill points). */
export function ensureStandingsComboProfileForRow(
  row: HTMLElement,
  overrides: { division?: number; seasonId?: number } = {}
): void {
  const meta = readStandingsRowMeta(row);
  if (!meta) return;
  if (overrides.division != null) meta.division = overrides.division;
  if (overrides.seasonId != null) meta.seasonId = overrides.seasonId;
  scheduleTooltipForRow(row, meta);
}

/** Affiche / masque le panneau détail points à droite du profil. */
export function setStandingsComboPointsHtml(html: string | null): void {
  const combo = getComboEl();
  if (!pointsEl) return;
  if (html == null || html === '') {
    if (!pointsEl.hidden) {
      const leavingPoints = pointsEl;
      closeTooltipSlideLeft(leavingPoints, () => {
        if (pointsEl !== leavingPoints) return;
        clearPointsPanelContent();
        syncComboClasses();
        if (tooltipAnchor && !combo.hidden) {
          positionCombo(tooltipAnchor);
        }
      });
      syncComboClasses();
    } else {
      clearPointsPanelContent();
      pointsEl.hidden = true;
      syncComboClasses();
    }
    return;
  }
  pointsEl.innerHTML = html;
  // --with-points avant le reflow du slide, sinon la transition est sautée.
  pointsEl.hidden = false;
  syncComboClasses();
  openTooltipFade(combo);
  openTooltipSlideLeft(pointsEl);
  if (tooltipAnchor) {
    positionCombo(tooltipAnchor);
    requestAnimationFrame(() => {
      if (tooltipAnchor) positionCombo(tooltipAnchor);
    });
  }
}

export function setStandingsComboPinned(pinned: boolean): void {
  holdOpen = pinned;
  syncComboClasses();
  if (!pinned && !hoveredRow) {
    hideCombo();
  }
}

export function standingsComboContains(node: Node): boolean {
  return comboEl?.contains(node) === true;
}

export function isStandingsComboVisible(): boolean {
  return comboEl != null && !comboEl.hidden && !comboEl.classList.contains('is-tooltip-leaving');
}

export function clearStandingsComboPoints(): void {
  if (holdOpen) return;
  setStandingsComboPointsHtml(null);
}

function isEnteringRow(e: MouseEvent, row: HTMLElement): boolean {
  const from = e.relatedTarget;
  return !(from instanceof Node) || !row.contains(from);
}

function isLeavingRow(e: MouseEvent, row: HTMLElement): boolean {
  const to = e.relatedTarget;
  return !(to instanceof Node) || !row.contains(to);
}

function ensureGlobalDismissListeners(): void {
  if (globalDismissListeners) return;
  globalDismissListeners = true;
  window.addEventListener(
    'scroll',
    () => {
      if (!holdOpen) dismissTooltip();
    },
    { capture: true, passive: true }
  );
  window.addEventListener(
    'resize',
    () => {
      if (!holdOpen) dismissTooltip();
    },
    { passive: true }
  );
  window.addEventListener('blur', () => {
    if (!holdOpen) dismissTooltip();
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden' && !holdOpen) dismissTooltip();
  });
}

const boundContainers = new WeakSet<ParentNode>();

/** Lie le survol / focus des lignes `[data-driver-id]` au tooltip pilote. */
export function bindStandingsDriverTooltips(
  container: ParentNode,
  options: { division?: number; seasonId?: number } = {}
): void {
  if (boundContainers.has(container)) return;
  boundContainers.add(container);

  const division = options.division;
  const seasonId = options.seasonId;

  container.addEventListener('mouseover', (e) => {
    if (!(e.target instanceof HTMLElement)) return;
    if (e.target.closest('.standings-points-pill--tip')) return;
    const row = e.target.closest<HTMLElement>(ROW_SELECTOR);
    if (!row || !container.contains(row)) return;
    if (!isEnteringRow(e as MouseEvent, row)) return;
    if (holdOpen) return;

    const meta = readStandingsRowMeta(row);
    if (!meta) return;
    if (division != null) meta.division = division;
    if (seasonId != null) meta.seasonId = seasonId;

    scheduleTooltipForRow(row, meta);
  });

  container.addEventListener('mouseout', (e) => {
    if (holdOpen) return;
    if (!(e.target instanceof HTMLElement)) return;
    const row = e.target.closest<HTMLElement>(ROW_SELECTOR);
    if (!row || !isLeavingRow(e as MouseEvent, row)) return;
    const to = (e as MouseEvent).relatedTarget;
    if (to instanceof Node && standingsComboContains(to)) return;
    if (!isActiveDriverRow(row)) return;
    hoveredRow = null;
    cancelPendingShow();
    hideCombo();
  });

  container.addEventListener('mouseleave', (e) => {
    if (holdOpen) return;
    const related = (e as MouseEvent).relatedTarget;
    if (related instanceof Node && standingsComboContains(related)) return;
    dismissTooltip();
  });

  container.addEventListener('focusin', (e) => {
    if (!(e.target instanceof HTMLElement)) return;
    if (e.target.closest('.standings-points-pill--tip')) return;
    const row = e.target.closest<HTMLElement>(ROW_SELECTOR);
    if (!row || !container.contains(row)) return;
    if (holdOpen) return;

    const meta = readStandingsRowMeta(row);
    if (!meta) return;
    if (division != null) meta.division = division;
    if (seasonId != null) meta.seasonId = seasonId;

    scheduleTooltipForRow(row, meta);
  });

  container.addEventListener('focusout', (e) => {
    if (holdOpen) return;
    if (!(e.target instanceof HTMLElement)) return;
    const row = e.target.closest<HTMLElement>(ROW_SELECTOR);
    if (!row || !isLeavingRow(e as MouseEvent, row)) return;
    if (!isActiveDriverRow(row)) return;
    hoveredRow = null;
    cancelPendingShow();
    hideCombo();
  });
}

/** Retire le tooltip flottant (changement de vue). */
export function removeStandingsDriverTooltip(): void {
  holdOpen = false;
  hoveredRow = null;
  cancelPendingShow();
  tooltipAnchor = null;
  cancelTooltipLeave(comboEl);
  cancelTooltipLeave(pointsEl);
  if (pointsEl) {
    pointsEl.hidden = true;
    pointsEl.innerHTML = '';
    pointsEl.style.height = '';
    pointsEl.style.maxHeight = '';
    pointsEl.classList.remove('is-slide-open', 'is-slide-leaving');
  }
  if (comboEl) {
    comboEl.hidden = true;
    comboEl.remove();
  }
  comboEl = null;
  profileEl = null;
  pointsEl = null;
  comboHoverListeners = false;
  driverCache.clear();
  profileCache.clear();
}

/** @internal Tests */
export function __standingsTooltipTestState(): {
  hoveredRow: HTMLElement | null;
  showGeneration: number;
  isVisible: boolean;
  hasPointsPanel: boolean;
  comboEl: HTMLDivElement | null;
  isPinned: boolean;
} {
  return {
    hoveredRow,
    showGeneration,
    isVisible: isStandingsComboVisible(),
    hasPointsPanel: isTooltipSlideOpen(pointsEl),
    comboEl,
    isPinned: holdOpen,
  };
}

/** @internal Tests — force le layout (hauteur points + position). */
export function __repositionStandingsComboForTests(): void {
  if (tooltipAnchor && comboEl && !comboEl.hidden) {
    positionCombo(tooltipAnchor);
  }
}

/** @internal Tests */
export function __resetStandingsTooltipTestState(): void {
  removeStandingsDriverTooltip();
}
